import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  deletePlaceNote,
  updateNoteUpvotes,
} from "../../services/placeNoteService";
import { PlaceNote } from "../../types/PlaceNote";
import { User, UserProfileLinkFragment } from "../../types/User";
import { formatTimestampNoTime } from "../../utils/format-timestamp";
import { PublicUserProfileLink } from "../PublicUserProfileLink/PublicUserProfileLink";
import "./NoteCard.scss";

interface NoteCardProps {
  note: PlaceNote;
  user?: User | null;
}

export const NoteCard: React.FC<NoteCardProps> = ({ note, user }) => {
  const [upvoteCount, setUpvoteCount] = useState(note.upvotes);

  const handleDelete = useCallback(async () => {
    if (!user) {
      return toast.error("Please login to delete your note");
    }
    const confirmed = window.confirm(
      "Are you sure you want to delete this note?"
    );
    if (!confirmed) {
      return;
    }
    await deletePlaceNote(note.id);
    window.location.reload();
  }, [note, user]);

  const handleUpvote = useCallback(async () => {
    if (!user) {
      return toast.error("Please login to be a part of the community");
    }

    if (note.voterUserIds.includes(user.uid)) {
      return toast.success("You have already upvoted this note");
    }

    setUpvoteCount(upvoteCount + 1);
    await updateNoteUpvotes(note.id, user.uid);
  }, [note, user, upvoteCount]);

  const attribution = useMemo(() => {
    if (note.creatorUsername && note.creatorProfilePictureUrl) {
      const creatorProfile: UserProfileLinkFragment = {
        username: note.creatorUsername ?? "",
        photoURL: note.creatorProfilePictureUrl,
      };
      return (
        <div className="note-card-attribution">
          <PublicUserProfileLink
            profile={creatorProfile}
            linkToProfile
            size="sm"
          />
        </div>
      );
    }
    return null;
  }, [note]);

  const upvotes = useMemo(() => {
    return (
      <div className="note-card-upvotes">
        {note.creatorId === user?.uid && (
          <span
            className="material-symbols-outlined upvote-icon"
            onClick={handleDelete}
          >
            delete
          </span>
        )}
        <span
          className="material-symbols-outlined upvote-icon"
          onClick={handleUpvote}
        >
          expand_circle_up
        </span>
        <span className="upvote-count">{upvoteCount}</span>
      </div>
    );
  }, [note, user, handleDelete, handleUpvote, upvoteCount]);

  return (
    <div className="note-card">
      <div className="note-card-header">
        <div className="timestamp">{formatTimestampNoTime(note.createdAt)}</div>
      </div>
      <div className="note-card-body">{note.noteText}</div>
      <div className="note-card-footer">
        {attribution}
        {upvotes}
      </div>
    </div>
  );
};
